/**
 * Hook për Web Push – regjistron pajisjen për njoftime kur ndryshon output S475.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../config';

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSupported('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window);
    if ('Notification' in window) setPermission(Notification.permission);
    const checkExisting = async () => {
      if (!('serviceWorker' in navigator)) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) setRegistered(true);
        }
      } catch (_) {}
    };
    checkExisting();
  }, []);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const register = useCallback(async () => {
    if (!supported) {
      setError('Push nuk mbështetet në këtë shfletues');
      return false;
    }
    setLoading(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError('Leja u refuzua');
        setLoading(false);
        return false;
      }
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        reg = await navigator.serviceWorker.register('/sw.js');
      }
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${BACKEND_URL}/api/push/vapid-public`, { headers: getHeaders() });
      if (!data.enabled || !data.vapid_public_key) {
        setError('Push nuk është konfiguruar në server');
        setLoading(false);
        return false;
      }
      const keyBytes = urlBase64ToUint8Array(data.vapid_public_key);
      if (!keyBytes) {
        setError('Çelësi VAPID në server është i pavlefshëm');
        setLoading(false);
        return false;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes,
      });
      await axios.post(
        `${BACKEND_URL}/api/push/subscribe`,
        { subscription: sub.toJSON() },
        { headers: getHeaders() }
      );
      setRegistered(true);
      setLoading(false);
      return true;
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Gabim');
      setLoading(false);
      return false;
    }
  }, [supported]);

  const unregister = useCallback(async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/push/unsubscribe`, { headers: getHeaders() });
      setRegistered(false);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  return { supported, permission, registered, loading, error, register, unregister };
}

function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== 'string') return null;
  const raw = String(base64String).trim().replace(/\s/g, '');
  if (!raw.length) return null;
  try {
    const padding = '='.repeat((4 - (raw.length % 4)) % 4);
    const base64 = (raw + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  } catch (e) {
    console.error('VAPID key decode error', e);
    return null;
  }
}
